import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, forkJoin, map } from 'rxjs';
import {
  QuizzesService,
  ExamsService,
  CoursesService,
  BooksService,
  QuestionsService,
  LiveEventsService,
  AiService,
} from 'src/app/api/services';
import { QuestionModel } from 'src/app/api/models';
import { QuestionType, questionTypeDisplayFn } from 'src/app/app.types';
import { FormsModule } from '@angular/forms';
import { ImportCsvErrorDialogComponent } from 'src/app/cms/common/dialogs/import-csv-error-dialog.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ImportQuestionCsvErrorDialogComponent } from './import-question-csv-error-dialog/import-question-csv-error-dialog.component';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import {
  LIST_ITEM_HEIGHT,
  settingAnswers,
  settingData,
  validateQuestionAnswers,
  validateSchema,
} from 'src/app/utility/app.utils';
import { NavigationBreadcrumb } from 'src/app/cms/common/navigation-breadcrumbs/navigation-breadcrumbs.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CsvImportProgressDialogComponent } from 'src/app/cms/common/dialogs/csv-import-progress-dialog.component';
import { CancelDialogLabels } from 'src/app/cms/common/dialogs/cancel-confirmation.component';
import { NgTemplateOutlet, NgClass } from '@angular/common';
import { CsvImportUploadAreaComponent } from '../../../common/csv-import-upload-area.component';
import { MatIcon } from '@angular/material/icon';
import { ContentSpinnerComponent } from '../../../common/ui/content-spinner.component';
import { MatButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { MatList, MatListItem } from '@angular/material/list';
import { QuestionEditComponent } from '../../../edit/question-edit.component';
import { schemas } from 'src/app/payload-validators';
import { NgScrollbar } from 'ngx-scrollbar';

@Component({
  selector: 'qa-import-questions-csv',
  templateUrl: './import-questions-csv.component.html',
  styleUrls: ['./import-questions-csv.component.scss'],
  imports: [
    CsvImportUploadAreaComponent,
    MatIcon,
    ContentSpinnerComponent,
    FormsModule,
    NgTemplateOutlet,
    MatCheckbox,
    MatButton,
    ButtonComponent,
    NgClass,
    TranslateModule,
    QuestionEditComponent,
    MatList,
    MatListItem,
    NgScrollbar,
  ],
})
export class ImportQuestionsCsvComponent implements OnInit {
  @ViewChild(QuestionEditComponent) questionEdit: QuestionEditComponent;
  entityId: string;
  courseId: string;
  bookId: string;
  loading = false;
  questionTypeDisplayFn = questionTypeDisplayFn;
  questionTypeEnum = QuestionType;
  navigationBreadcrumb: NavigationBreadcrumb;
  data;
  removedItemIdx: number[] = [];
  currentItemIndex = 0;
  isChecked = true;
  showOverview = false;
  successQuestion = [];
  failedQuestion = [];
  isComplete = false;
  isChatGPT = false;
  inProgress = false;
  navigateForm = true;
  isExam = false;
  isLiveEvent = false;
  uploadAreaParameters = {
    csv: 'assets/import-csv-templates/Sample-import-questions.csv',
    excel: 'assets/import-csv-templates/Sample-import-questions.xlsx',
    csvError: false,
  };
  dialogRef: MatDialogRef<CsvImportProgressDialogComponent>;
  cancelUploadLabels: CancelDialogLabels = {
    messages: [
      this.translate.instant('csvImports.cancelConfirmation.message1'),
      this.translate.instant('csvImports.cancelConfirmation.message2'),
    ],
    buttonLabels: {
      confirm: this.translate.instant('common.button.continue'),
      cancel: this.translate.instant('Quit'),
    },
  };
  isPollQuestion = false;
  LIST_ITEM_HEIGHT = LIST_ITEM_HEIGHT;

  constructor(
    private matDialog: MatDialog,
    private exams: ExamsService,
    private liveEventsService: LiveEventsService,
    private matSnackBar: MatSnackBar,
    private quizzes: QuizzesService,
    private questionsService: QuestionsService,
    private coursesService: CoursesService,
    private booksService: BooksService,
    private translate: TranslateService,
    private aiService: AiService,
    private router: Router,
    route: ActivatedRoute
  ) {
    route.params.subscribe((params) => {
      if (params.id) {
        this.entityId = params.id;
      }
    });
    route.queryParams.subscribe((params) => {
      if (params.course) {
        this.courseId = params.course;
      }
      if (params.book) {
        this.bookId = params.book;
      }
      if (params.type === 'exam-question') {
        this.isExam = true;
      }
      if (params.type === 'event-question') {
        this.isLiveEvent = true;
      }
      if (params.isPoll) {
        this.isPollQuestion = true;
      }
      if (params.isChatGPT) {
        this.isChatGPT = true;
      }
    });
  }

  ngOnInit(): void {
    this.navigationBreadcrumb = {
      homeUrl: '/cms/courses',
      help: [{ id: 'teaser' }],
      currentPage: this.translate.instant(
        'content.question.addMenu.questionsFromExcelCSV'
      ),
      breadcrumbs: [],
    };
    if (this.isPollQuestion) {
      this.uploadAreaParameters.csv =
        'assets/import-csv-templates/Sample-import-questions-survey.csv';
      this.uploadAreaParameters.excel =
        'assets/import-csv-templates/Sample-import-questions.xlsx';
    }
    if (this.courseId || this.bookId || this.entityId) {
      this.getQuestionInfo();
    }

    if (this.isChatGPT) {
      this.data = {};
      this.navigateForm = false;
      this.loading = true;
      this.aiService
        .generateQuestion({ body: { quiz_id: parseInt(this.entityId, 0) } })
        .subscribe((data) => {
          const csvData = [];
          data.map((eachQuestion: any) => {
            eachQuestion.answers.map((eachAns: any) => {
              eachAns.is_right = eachAns.correct;
              eachAns.text = eachAns.answer;
            });
            csvData.push({
              text: eachQuestion.question,
              answers: eachQuestion.answers,
              type: 2,
            });
          });

          this.data.csvData = csvData;
          this.data.parentId = this.entityId;
          this.loading = false;
          this.navigateForm = true;
        });
    }
  }

  getQuestionInfo() {
    const requestArray = [];
    const parentRequest = this.courseId
      ? this.coursesService.getCourseById({
          id: Number(this.courseId),
          full: false,
        })
      : this.bookId
      ? this.booksService.getBookById({
          id: Number(this.bookId),
          full: false,
        })
      : null;
    if (parentRequest) {
      requestArray.push(parentRequest);
    }
    if (this.isExam) {
      if (this.entityId) {
        if (!isNaN(Number(this.entityId))) {
          requestArray.push(
            this.exams.getExamById({ id: Number(this.entityId) })
          );
        }
      }
    } else if (this.isLiveEvent) {
      requestArray.push(
        this.liveEventsService.getLiveEventById({ id: Number(this.entityId) })
      );
    } else {
      requestArray.push(
        this.quizzes.getQuizById({ id: Number(this.entityId) })
      );
    }
    forkJoin(requestArray).subscribe((results) => {
      if (results && results.length > 1) {
        const breadcrumbs = results.map((result) => {
          return {
            text: result.name,
          };
        });
        breadcrumbs[0]['route'] = this.courseId
          ? `/cms/courses/edit/${this.courseId}`
          : `/cms/books/edit/${this.bookId}`;
        breadcrumbs[1]['route'] = this.isExam
          ? `/cms/courses/edit/${this.courseId};exam=${this.entityId}`
          : this.courseId
          ? `/cms/courses/edit/${this.courseId};quiz=${this.entityId}`
          : `/cms/books/edit/${this.bookId};quiz=${this.entityId}`;
        this.navigationBreadcrumb.breadcrumbs = breadcrumbs;
      }
    });
  }

  uploadCsv(tableData) {
    try {
      if (tableData.length) {
        const csvDataArr = settingData(tableData);
        let questionsArr: any = {
          csvData: csvDataArr,
          parentId: this.entityId,
          navigationLabels: {
            prev: this.translate.instant(
              'csvImports.question.navigationLabel.prev'
            ),
            next: this.translate.instant(
              'csvImports.question.navigationLabel.next'
            ),
          },
        };
        questionsArr = this.handleCsvErrors(questionsArr);
        settingAnswers(questionsArr?.csvData ? questionsArr?.csvData : []);
        this.currentItemIndex = 0;
        this.data = questionsArr;
      }
      if (!this.data && !this.isPollQuestion) {
        this.openCsvErrorDialog();
        return;
      }
      this.removedItemIdx = [];
      this.currentItemIndex = 0;
      this.isChecked = true;
    } catch (error) {
      console.log(error);
    }
  }

  handleCsvErrors(questionsArr) {
    let idx = 0;
    const errorMsg = [];
    const isContainTrueFalse = questionsArr.csvData.find(
      (q) => q['type'] === 'True/False'
    );
    if (this.isPollQuestion && isContainTrueFalse) {
      this.matSnackBar.open(
        this.translate.instant('question.message.pollQuestionImportCsv')
      );
      return;
    }

    for (const question of questionsArr.csvData) {
      if (!question.text) {
        questionsArr = undefined;
        break;
      }
      question.is_poll_question = !!this.isPollQuestion;
      if (question['type'] === 'True/False') {
        question['type'] = 1;
        if (question['answers'].length && question['answers'][0]['is_right']) {
          question['is_right'] = question['answers'][0]['is_right'];
        } else {
          errorMsg.push({
            index: idx,
            message: this.translate.instant(
              'csvImports.question.errorMessages.trueFalse.error1'
            ),
          });
        }
        delete question['answers'];
      } else if (question['type'] === 'Multiple Choice') {
        question['type'] = 0;
        if (
          !question.answers.length ||
          this.checkAnswers(question.answers).length < 2
        ) {
          errorMsg.push({
            index: idx,
            message: this.translate.instant(
              'csvImports.question.errorMessages.multipleChoice.provideMinimumTwoOptions'
            ),
          });
        } else {
          for (const answer of question.answers) {
            if (
              (answer.text && !answer.is_right) ||
              (!answer.text && answer.is_right)
            ) {
              errorMsg.push({
                index: idx,
                message: this.translate.instant(
                  'csvImports.question.errorMessages.multipleChoice.mustHaveResult'
                ),
              });
              break;
            }
          }
        }
      } else if (question['type'] === 'Single Choice') {
        question['type'] = 2;
        if (
          !question.answers.length ||
          this.checkAnswers(question.answers).length < 2
        ) {
          errorMsg.push({
            index: idx,
            message: this.translate.instant(
              'csvImports.question.errorMessages.singleChoice.provideMinimumTwoOptions'
            ),
          });
        } else {
          const singleArr = this.checkAnswers(question.answers).filter(
            (x) => x.is_right.toLowerCase() === 'correct'
          );
          if (!singleArr.length) {
            errorMsg.push({
              index: idx,
              message: this.translate.instant(
                'csvImports.question.errorMessages.singleChoice.noCorrectAnswer'
              ),
            });
          } else if (singleArr.length > 1) {
            errorMsg.push({
              index: idx,
              message: this.translate.instant(
                'csvImports.question.errorMessages.singleChoice.multipleAnswersProvided'
              ),
            });
          } else {
            for (const answer of question.answers) {
              if (
                (answer.text && !answer.is_right) ||
                (!answer.text && answer.is_right)
              ) {
                errorMsg.push({
                  index: idx,
                  message: this.translate.instant(
                    'csvImports.question.errorMessages.singleChoice.mustHaveResult'
                  ),
                });
                break;
              }
            }
          }
        }
      } else {
        errorMsg.push({
          index: idx,
          message: this.translate.instant(
            'csvImports.question.errorMessages.questionTypeError'
          ),
        });
      }
      idx++;
    }
    return this.displayCsvErrors(errorMsg, questionsArr);
  }

  displayCsvErrors(errorMsg, questionsArr) {
    if (!questionsArr) {
      return questionsArr;
    }
    if (errorMsg.length === questionsArr.csvData.length) {
      questionsArr = undefined;
    } else if (errorMsg.length) {
      this.matDialog.open(ImportQuestionCsvErrorDialogComponent, {
        data: { total: questionsArr.csvData.length, questions: errorMsg },
      });
      const failedIdx = errorMsg.map((x) => x.index);
      const csvArr = [];
      for (
        let eachCsvData = 0;
        eachCsvData < questionsArr.csvData.length;
        eachCsvData++
      ) {
        if (!failedIdx.includes(eachCsvData)) {
          csvArr.push(questionsArr.csvData[eachCsvData]);
        }
      }
      questionsArr.csvData = csvArr;
    }
    return questionsArr;
  }
  checkAnswers(answers) {
    if (!answers.length) {
      return [];
    }
    const keys = Object.keys(answers[0]);
    return answers.filter((c) => {
      return keys.every((k) => {
        return c[k];
      });
    });
  }

  onNavigate(currentQuestionIndex: number) {
    if (currentQuestionIndex < this.data.csvData.length) {
      this.loading = true;
      this.navigateForm = false;
      this.isChecked = this.removedItemIdx.includes(currentQuestionIndex)
        ? false
        : true;
      setTimeout(() => {
        this.navigateForm = true;
        this.loading = false;
        this.currentItemIndex = currentQuestionIndex;
      }, 10);
    } else {
      this.showOverview = true;
      if (this.navigationBreadcrumb) {
        this.navigationBreadcrumb.currentPage = this.translate.instant(
          'csvImports.question.importQuestionsOverview'
        );
      }
    }
  }

  onChange(event, index?: number) {
    if (index === this.currentItemIndex && (index || index === 0)) {
      this.isChecked = event.checked;
    }
    if (event.checked) {
      const remIdx = index || index === 0 ? index : this.currentItemIndex;
      const idx = this.removedItemIdx.findIndex((x) => x === remIdx);
      this.removedItemIdx.splice(idx, 1);
    } else {
      const idx = index || index === 0 ? index : this.currentItemIndex;
      if (!this.removedItemIdx.includes(idx)) {
        this.removedItemIdx.push(idx);
      }
    }
  }

  overviewData() {
    this.showOverview = true;
    if (this.navigationBreadcrumb) {
      this.navigationBreadcrumb.currentPage = this.translate.instant(
        'csvImports.question.importQuestionsOverview'
      );
    }
  }

  importCsv() {
    const validateAnswers = validateQuestionAnswers(
      this.data.csvData,
      this.translate
    );
    if (validateAnswers?.hasError) {
      this.matSnackBar.open(validateAnswers.error);
      return;
    }
    this.dialogRef = this.matDialog.open(CsvImportProgressDialogComponent, {
      disableClose: true,
      data: this.getModalData(false),
    });
    this.addQuestions(0);
    this.dialogRef.afterClosed().subscribe(() => {
      this.backToList();
    });
  }

  addQuestions(idx: number) {
    if (!this.removedItemIdx.includes(idx)) {
      const newQuestion: QuestionModel = this.data.csvData[idx];
      if (newQuestion.media && !newQuestion.media.id) {
        newQuestion.media = null;
      }
      if (newQuestion.type === QuestionType.TRUE_FALSE) {
        delete newQuestion.answers;
        newQuestion.is_right = !!newQuestion.is_right;
      } else {
        newQuestion.answers = newQuestion.answers.map((answer) => {
          answer.is_right = Boolean(answer.is_right);
          return answer;
        });
      }
      let saveQuestion$: Observable<QuestionModel[]>;
      const sentryInfo = {
        title: `Error while importing questions from CSV`,
        file: 'import-questions-csv.component.ts - addQuestions',
        api: '',
      };
      if (this.entityId) {
        if (this.isExam) {
          sentryInfo.api = 'copyQuestionToExam';
          saveQuestion$ = this.exams.copyQuestionToExam({
            id: this.data.parentId,
            body: [newQuestion],
          });
        } else if (this.isLiveEvent) {
          sentryInfo.api = 'copyQuestionToEvent';
          saveQuestion$ = this.liveEventsService.copyQuestionToEvent({
            id: this.data.parentId,
            body: [newQuestion],
          });
        } else {
          sentryInfo.api = 'copyQuestionToQuiz';
          saveQuestion$ = this.quizzes.copyQuestionToQuiz({
            id: this.data.parentId,
            body: [newQuestion],
          });
        }
      } else {
        sentryInfo.api = 'createQuestion';
        saveQuestion$ = this.questionsService
          .createQuestion({
            body: newQuestion,
          })
          .pipe(map((q) => [q]));
      }
      const validateQuestionsPayload = validateSchema(
        schemas.QuestionModel,
        newQuestion,
        this.matSnackBar,
        sentryInfo
      );
      if (validateQuestionsPayload?.isInvalidSchema) {
        this.handleApiCalls(idx);
        return;
      }
      saveQuestion$.subscribe(
        () => {
          this.successQuestion.push(idx);
          if (this.dialogRef && this.dialogRef.componentInstance) {
            this.dialogRef.componentInstance.data = this.getModalData(false);
          }
          this.handleApiCalls(idx);
        },
        () => {
          this.failedQuestion.push(idx);
          if (this.dialogRef && this.dialogRef.componentInstance) {
            this.dialogRef.componentInstance.data = this.getModalData(false);
          }
          this.handleApiCalls(idx);
        }
      );
    } else {
      this.handleApiCalls(idx);
    }
  }

  handleApiCalls(idx: number) {
    idx++;
    if (idx < this.data.csvData.length) {
      this.addQuestions(idx);
    } else {
      setTimeout(() => {
        this.isComplete = true;
        this.inProgress = false;
        if (this.dialogRef && this.dialogRef.componentInstance) {
          this.dialogRef.componentInstance.data = this.getModalData(true);
        }
      }, 500);
    }
  }

  getModalData(isComplete: boolean) {
    return {
      total: this.data.csvData.length - this.removedItemIdx.length,
      success: this.successQuestion.length,
      failed: this.failedQuestion.length,
      isProgress: !isComplete,
      isComplete,
      label: {
        progress: 'Importing Questions',
        success: this.translate.instant(
          'content.question.csvUpload.successUploadMessage'
        ),
      },
    };
  }

  backToList() {
    const query = { showPollQuestion: true };
    const queryParams = this.courseId
      ? {
          courseId: this.courseId,
        }
      : this.bookId
      ? { bookId: this.bookId }
      : {};
    if (this.entityId) {
      if (this.isExam) {
        this.router.navigate([`/cms/exams/${this.entityId}`], { queryParams });
      } else if (this.isLiveEvent) {
        this.router.navigate([`/cms/live-events/${this.entityId}`], {
          queryParams,
        });
      } else {
        if (this.courseId) {
          this.router.navigate([
            `/cms/courses/edit/${this.courseId}`,
            { course: this.courseId, quiz: this.entityId },
          ]);
        } else if (this.bookId) {
          this.router.navigate([
            `/cms/books/edit/${this.bookId}`,
            { book: this.bookId, quiz: this.entityId },
          ]);
        } else {
          this.router.navigate([`/cms/content/${this.entityId}/questions`]);
        }
      }
    } else {
      this.router.navigate(['/cms/content/questions'], {
        queryParams: this.isPollQuestion ? query : {},
      });
    }
  }

  back() {
    this.showOverview = false;
    if (this.navigationBreadcrumb) {
      this.navigationBreadcrumb.currentPage = this.translate.instant(
        'content.question.addMenu.questionsFromExcelCSV'
      );
    }
    this.isChecked = this.removedItemIdx.includes(this.currentItemIndex)
      ? false
      : true;
  }

  editCard(index: number) {
    this.currentItemIndex = index;
    this.isChecked = this.removedItemIdx.includes(index) ? false : true;
    this.back();
  }

  cancel() {
    if (this.isComplete || this.isChatGPT) {
      this.backToList();
    } else {
      this.data = undefined;
      this.showOverview = false;
      if (this.navigationBreadcrumb) {
        this.navigationBreadcrumb.currentPage = this.translate.instant(
          'content.question.addMenu.questionsFromExcelCSV'
        );
      }
    }
  }

  onSelectAll() {
    if (!this.isComplete) {
      if (this.removedItemIdx.length) {
        this.removedItemIdx = [];
      } else {
        for (
          let eachCsvData = 0;
          eachCsvData < this.data.csvData.length;
          eachCsvData++
        ) {
          this.removedItemIdx.push(eachCsvData);
        }
      }
    }
  }

  onNext(event: string) {
    if (event) {
      this.uploadCsv(event);
    }
  }

  onCancel(event) {
    if (event) {
      this.backToList();
    }
  }

  openCsvErrorDialog(): void {
    this.matDialog
      .open(ImportCsvErrorDialogComponent, {
        data: {
          text: this.translate.instant(
            'common.message.csvUploadError.textQuiz'
          ),
          templateUrl: {
            link1: {
              title: this.translate.instant(
                'common.message.csvUploadError.excelCSV'
              ),
              href: 'https://quizacademy.de/tipps/excel-csv-inhalte-importieren/',
            },
            link2: {
              title: this.translate.instant(
                'common.message.csvUploadError.kahoot'
              ),
              href: 'https://quizacademy.de/tipps/kahoot-inhalte-importieren/',
            },
          },
        },
      })
      .afterClosed()
      .subscribe(() => {
        this.uploadAreaParameters.csvError = true;
        this.uploadAreaParameters = Object.assign(
          {},
          this.uploadAreaParameters
        );
      });
  }
}
